import React, { useState } from "react";

interface Parameter {
  name: string;
  description: string;
  required?: boolean;
}

interface Props {
  name: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  path: string;
  description: string;
  example?: string;
  queryParameters?: Parameter[];
  bodyParameters?: Parameter[];
}

const DocSection: React.FC<Props> = ({
  name,
  method,
  path,
  description,
  example,
  queryParameters,
  bodyParameters,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const methodStyles = {
    GET: "bg-blue-50 text-blue-700 border-blue-200",
    POST: "bg-emerald-50 text-emerald-700 border-emerald-200",
    PUT: "bg-amber-50 text-amber-700 border-amber-200",
    DELETE: "bg-rose-50 text-rose-700 border-rose-200",
  };

  return (
    <div className="bg-white border border-gallery-200 rounded-sm overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gallery-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <span className={`inline-block px-2.5 py-1 text-xs font-mono font-medium rounded-sm border ${methodStyles[method]}`}>
            {method}
          </span>
          <div>
            <h3 className="font-medium text-gallery-900">{name}</h3>
            <code className="text-sm text-gallery-500 font-mono">{path}</code>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-gallery-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gallery-100">
          <p className="text-sm text-gallery-600 mt-4 mb-6">{description}</p>

          {/* Query Parameters */}
          {queryParameters && queryParameters.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
                Query Parameters
              </h4>
              <div className="border border-gallery-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gallery-50 border-b border-gallery-200">
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Parameter</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gallery-100">
                    {queryParameters.map((param) => (
                      <tr key={param.name}>
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono bg-gallery-100 px-1.5 py-0.5 rounded text-gallery-800">
                            {param.name}
                          </code>
                          {param.required && (
                            <span className="ml-2 text-xs text-rose-500">required</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gallery-600">{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Body Parameters */}
          {bodyParameters && bodyParameters.length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
                Body Parameters
              </h4>
              <div className="border border-gallery-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gallery-50 border-b border-gallery-200">
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Parameter</th>
                      <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gallery-500">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gallery-100">
                    {bodyParameters.map((param) => (
                      <tr key={param.name}>
                        <td className="px-4 py-3">
                          <code className="text-xs font-mono bg-gallery-100 px-1.5 py-0.5 rounded text-gallery-800">
                            {param.name}
                          </code>
                          {param.required && (
                            <span className="ml-2 text-xs text-rose-500">required</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gallery-600">{param.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Example Response */}
          {example && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider text-gallery-500 mb-3">
                Example Response
              </h4>
              <div className="bg-gallery-900 rounded-sm p-4 overflow-x-auto">
                <pre className="text-sm font-mono text-gallery-100 whitespace-pre-wrap">
                  {example}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocSection;
